/**
 * @fileoverview Profile modal body.
 * @description The tabbed body rendered inside ProfileModal: the hero (avatar, name, role, status),
 * an Overview / Security (/ Notifications) tab strip, and the active tab's panel. It owns the tab state and reads the
 * signed-in user's identity (GET /identity/me via useCurrentSession, with the auth store's own
 * email/role as the fallback while that resolves). Because the shared Modal stops rendering its
 * children the moment it closes, this component unmounts on close and the tab always resets to
 * Overview on reopen. The Notifications tab only exists when the app layer passes its body in.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import ProfileHero from '@/features/auth/components/ProfileHero'
import ProfileOverviewTab from '@/features/auth/components/ProfileOverviewTab'
import ProfileSecurityTab from '@/features/auth/components/ProfileSecurityTab'
import { useProfileIdentity } from '@/features/auth/hooks/useProfileIdentity'

import type { ReactNode } from 'react'

type ProfileTab = 'overview' | 'security' | 'notifications'

const BASE_TABS: readonly ProfileTab[] = ['overview', 'security']
const TABS_WITH_NOTIFICATIONS: readonly ProfileTab[] = [...BASE_TABS, 'notifications']

interface ProfileModalContentProps {
  notificationsTab?: ReactNode | undefined
}

interface ProfileTabButtonProps {
  tab: ProfileTab
  active: boolean
  onSelect: (tab: ProfileTab) => void
}

/**
 * @component
 * @param {ProfileTabButtonProps} props - One tab of the strip.
 * @param {ProfileTab} props.tab - Which tab the button selects.
 * @param {boolean} props.active - Whether it is the open tab.
 * @param {(tab: ProfileTab) => void} props.onSelect - Called with the tab when clicked.
 */
function ProfileTabButton({ tab, active, onSelect }: ProfileTabButtonProps) {
  const { t } = useTranslation()

  /**
   * @description Selects this button's tab.
   */
  const select = () => onSelect(tab)

  return (
    <button
      type="button"
      role="tab"
      id={`profile-tab-${tab}`}
      data-testid={`profile-tab-${tab}`}
      aria-selected={active}
      aria-controls={`profile-panel-${tab}`}
      onClick={select}
      className={`-mb-px cursor-pointer border-b-2 py-3 text-sm ${
        active
          ? 'border-primary font-semibold text-primary'
          : 'border-transparent font-medium text-slate hover:text-ink'
      }`}
    >
      {t(`profile.tabs.${tab}`)}
    </button>
  )
}

/**
 * @component
 * @param {ProfileModalContentProps} props - The optional Notifications tab body.
 * @param {ReactNode} [props.notificationsTab] - Rendered under the Notifications tab; the tab is
 * hidden when omitted.
 */
function ProfileModalContent({ notificationsTab }: ProfileModalContentProps) {
  const { t } = useTranslation()
  const { principal, email, displayName, roleName, initials } = useProfileIdentity()
  const [activeTab, setActiveTab] = useState<ProfileTab>('overview')
  const tabs = notificationsTab ? TABS_WITH_NOTIFICATIONS : BASE_TABS

  /**
   * @description Switches to the Security tab (the Overview's Manage link).
   */
  const showSecurity = () => setActiveTab('security')

  return (
    <>
      <ProfileHero
        displayName={displayName}
        initials={initials}
        roleName={roleName}
        status={principal?.status}
      />

      <div
        role="tablist"
        aria-label={t('profile.tabsLabel')}
        data-testid="profile-tabs"
        className="-mx-6 mt-5 flex gap-6 border-b border-divider px-6"
      >
        {tabs.map((tab) => (
          <ProfileTabButton
            key={tab}
            tab={tab}
            active={activeTab === tab}
            onSelect={setActiveTab}
          />
        ))}
      </div>

      <div
        role="tabpanel"
        id={`profile-panel-${activeTab}`}
        data-testid={`profile-panel-${activeTab}`}
        aria-labelledby={`profile-tab-${activeTab}`}
        className="pt-5"
      >
        {activeTab === 'overview' && (
          <ProfileOverviewTab
            email={email}
            roleName={roleName}
            principal={principal}
            onManageSecurity={showSecurity}
          />
        )}
        {activeTab === 'security' && <ProfileSecurityTab />}
        {activeTab === 'notifications' && notificationsTab}
      </div>
    </>
  )
}

export default ProfileModalContent
